import { useMutation } from '@tanstack/react-query'
import { apiClient, handleApiError } from '@/infrastructure/api/client'

/** Downloads one investigation's full translation transfer document as a
 * JSON blob (TRANSLATION-PLAN.md-adjacent feature — per-investigation
 * export/import, not the dashboard's read-only state). */
export async function exportTranslations(investigationId: string): Promise<Blob> {
  try {
    const response = await apiClient.get<Blob>(`/translations/${investigationId}/export`, {
      responseType: 'blob',
    })
    return response.data
  } catch (error) {
    throw handleApiError(error)
  }
}

export interface ImportTranslationsVariables {
  investigationId: string
  file: File
  clientId: string
}

/** Uploads a previously exported translation transfer file back for one
 * investigation — updates existing translation rows only. Takes the target
 * investigation id per-call (not at hook-creation time) so a single shared
 * hidden `<input type="file">` on the dashboard can drive the import for
 * whichever row triggered it. `clientId` correlates this upload with the
 * caller's already-open `import/progress/:clientId` SSE channel — the
 * import itself runs in the background on the server, so this request
 * resolves as soon as the file is validated and accepted (HTTP 202), not
 * once the import finishes; progress and the final result arrive over that
 * SSE channel instead (see the `EventSource` wiring in `page.tsx`). */
export function useImportTranslations() {
  return useMutation({
    mutationFn: async ({
      investigationId,
      file,
      clientId,
    }: ImportTranslationsVariables): Promise<void> => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('clientId', clientId)

        // The shared apiClient sets a default 'application/json' Content-Type
        // header. Axios's transformRequest special-cases FormData when the
        // Content-Type looks like JSON: it JSON-stringifies the FormData's
        // entries instead of sending it as multipart -- since a File has no
        // enumerable properties, it serializes to an empty object, silently
        // dropping the file. Clearing the header lets axios/the browser set
        // the correct multipart Content-Type with boundary instead.
        await apiClient.post(`/translations/${investigationId}/import`, formData, {
          headers: { 'Content-Type': undefined },
        })
      } catch (error) {
        throw handleApiError(error)
      }
    },
  })
}
